<!--
  AdminSupportStaffPicker.vue

  Step 1 of the admin fallback gate (D5/D6 of
  docs/exec-plans/completed/2026-06-23-admin-otp-gate-validateSecurityPermissionNoPrompt.md,
  mirrored from Retail POS into Restaurant POS).

  Renders a Bootstrap-styled dropdown of active support staff. The operator
  picks one and the component:
    1. Calls `supportStaffGate.requestOtp(userID, storeDB)` to trigger the
       server-side OTP send (and to populate the encrypted
       `localStorage.users` envelope the OTP step needs for "Resend OTP").
    2. Emits `'selected'` with the chosen user — the parent
       AdminSupportStaffModal switches to the OTP step.

  If the operator cancels, emits `'cancel'` and the parent resolves the gate
  promise as `false`.

  This mirrors the user-picking behaviour in
  `src/components/login/steps/UserSelectionStep.vue` but lives outside the
  login flow so it can be triggered from `UserSecurityChecker.vue#checkSecurity`
  when the supervisor whose PIN just verified is `Admin`.
-->
<template>
  <div class="admin-picker-step">
    <h4 class="card-title mb-3 text-uppercase">
      <span class="gradient-text">Select</span>
      <span class="text-primary-color">Support Staff</span>
    </h4>
    <p class="text-secondary mb-4 small">
      Admin override for <strong>{{ security }}</strong> requires a support
      staff on record. Pick the support staff authorising this action.
    </p>

    <form @submit.prevent="handleSubmit">
      <div class="form-floating select-container mb-3">
        <select
          class="form-select custom-select"
          v-model="selectedUser"
          id="adminGateUserSelect"
          aria-label="Support staff selection"
          required
          :disabled="loading"
        >
          <option value="" disabled selected>Select a support staff</option>
          <option v-for="user in users" :key="user.id" :value="user">
            {{ user.name || user.username || user.email }}
          </option>
        </select>
        <label for="adminGateUserSelect">Support Staff</label>
      </div>

      <p class="text-danger error-message" v-if="errorMessage">{{ errorMessage }}</p>

      <div class="d-flex gap-2 justify-content-center mt-3">
        <button
          type="button"
          class="btn btn-outline-secondary"
          @click="$emit('cancel')"
          :disabled="loading"
        >
          Cancel
        </button>
        <button
          type="submit"
          class="btn btn-primary user-button"
          :disabled="loading || !selectedUser"
        >
          <span class="button-text">{{ loadText }}</span>
          <span
            v-if="loading"
            class="spinner-grow spinner-grow-sm"
            role="status"
            aria-hidden="true"
          ></span>
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import { supportStaffGate } from '@/services/supportStaffGate';
import { Storage } from '@/services/Storage';

export default {
  name: 'AdminSupportStaffPicker',
  props: {
    /**
     * The permission key being gated (e.g. 'Loggoff', 'BelowCostSale').
     * Surfaced in the header copy so the operator knows what they're
     * authorising.
     */
    security: { type: String, required: true },
  },
  emits: ['selected', 'cancel'],
  data() {
    return {
      selectedUser: null,
      users: [],
      loading: false,
      errorMessage: '',
    };
  },
  computed: {
    loadText() {
      return this.loading ? 'Sending OTP...' : 'Continue';
    },
  },
  async mounted() {
    await this.fetchUsers();
  },
  methods: {
    async fetchUsers() {
      this.loading = true;
      this.errorMessage = '';
      try {
        this.users = await supportStaffGate.fetchSupportStaff();
      } catch (error) {
        this.errorMessage = 'Error fetching support staff. Please try again.';
        console.error('[AdminSupportStaffPicker] fetchSupportStaff failed:', error);
      } finally {
        this.loading = false;
      }
    },

    async handleSubmit() {
      if (!this.selectedUser) return;
      this.loading = true;
      this.errorMessage = '';
      try {
        const store = await Storage.getDefaultStore();
        const storeDB = store && store.StoreDB;
        await supportStaffGate.requestOtp(this.selectedUser.id, storeDB);
        this.$emit('selected', this.selectedUser);
      } catch (error) {
        this.errorMessage = 'Failed to request OTP. Please try again.';
        console.error('[AdminSupportStaffPicker] requestOtp failed:', error);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.admin-picker-step {
  padding: 0.5rem 0;
}
.card-title {
  font-weight: 700;
  letter-spacing: 1.2px;
  text-align: center;
  font-size: 1.4rem;
}
.text-primary-color {
  color: var(--text-primary);
}
.gradient-text {
  background: linear-gradient(to right, var(--accent-electric-blue), var(--accent-neon-purple), var(--accent-electric-blue));
  background-size: 200% 100%;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}
.select-container {
  position: relative;
}
.custom-select {
  border: var(--card-border);
  background-color: var(--card-bg);
  color: var(--text-primary);
  border-radius: 12px;
  padding: 0.75rem 1rem;
  height: 56px;
  font-size: 1rem;
  appearance: none;
  -webkit-appearance: none;
  cursor: pointer;
  &:focus {
    border-color: var(--accent-electric-blue);
    outline: none;
    box-shadow: 0 0 0 3px rgba(51, 156, 255, 0.25);
  }
}
.form-floating label {
  padding: 0.75rem 1rem;
  color: var(--text-secondary);
}
.error-message {
  min-height: 20px;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
}
.user-button {
  min-width: 140px;
  background: linear-gradient(to right, var(--accent-electric-blue), var(--accent-neon-purple));
  color: white;
  border: none;
  font-weight: 600;
  &:hover:not(:disabled) {
    transform: translateY(-1px);
  }
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
  .spinner-grow {
    margin-left: 8px;
  }
}
</style>